// frontend/src/pages/dashboards/gridLayout.js
// Phase 4 step 14a-2: the dashboard grid's rules. Pure: no React, no DOM.
// Named gridLayout.js, not layout.js, because pages/builder/layout.js is the
// builder's pane layout and two files of one name invite the wrong import.
//
// The grid is 12 columns wide and grows downwards in rows of 40 px with 12 px
// gaps. A tile is {key, kind, x, y, w, h} in grid units; `key` is the client's
// stable id (a saved tile's id, or a made-up one for a tile not saved yet).
//
// Gravity: after every change each tile rises as far as it can (the vertical
// compaction Grafana, Metabase and react-grid-layout apply), so a layout never
// has holes above a tile and the result is the same whatever the pointer did on
// the way. A dragged tile swaps with a tile it is dropped on: its top has to
// reach the other tile's top, then the other one moves to the space it left.
//
// The server (backend/app/dashboards/layout.py) checks structure only: bounds,
// a generic minimum (chart 2x2, text 1x1), no overlap, 40 tiles. The minimums
// per visual below are larger and live here, because a chart's type can change
// after it is placed. A tile already smaller than its visual's minimum (its
// chart became a table) is drawn as it is and grows to the minimum only when
// someone resizes it, so opening a dashboard never marks it changed.

export const COLUMNS = 12;
export const ROW_HEIGHT = 40;
export const GAP = 12;
export const STACK_BELOW = 900;          // narrower than this: one column, tiles in reading order
export const MAX_TILES = 40;
export const MAX_ROWS = 400;             // y + h never exceeds this
export const MAX_TILE_HEIGHT = 40;

export const MIN_SIZE = {
  kpi: { w: 3, h: 3 },        // 3 rows = 144 px: the title row, a 48 px number and its label (2 rows cut the number off)
  chart: { w: 4, h: 4 },
  table: { w: 6, h: 5 },
  text: { w: 3, h: 1 },
};

export const DEFAULT_SIZE = {
  kpi: { w: 3, h: 3 },
  chart: { w: 6, h: 5 },
  table: { w: 12, h: 6 },
  text: { w: 12, h: 1 },
};

const KPI_TYPES = new Set(['card', 'gauge']);
const TABLE_TYPES = new Set(['table', 'matrix']);

/** 'kpi' | 'chart' | 'table' | 'text'. A chart tile reads the type from its chart summary. */
export function visualOf(tile) {
  if (tile.kind === 'text') return 'text';
  const type = tile.chart ? tile.chart.chart_type : tile.chartType;
  if (KPI_TYPES.has(type)) return 'kpi';
  if (TABLE_TYPES.has(type)) return 'table';
  return 'chart';                       // includes a tile whose chart was deleted
}

export const minSize = (tile) => MIN_SIZE[visualOf(tile)];
export const defaultSize = (visual) => DEFAULT_SIZE[visual] || DEFAULT_SIZE.chart;

const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

export function overlaps(a, b) {
  return a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;
}

const sideBySide = (a, b) => a.x < b.x + b.w && b.x < a.x + a.w;

/** The first free row below every tile. */
export function bottom(tiles) {
  return tiles.reduce((m, t) => Math.max(m, t.y + t.h), 0);
}

/** Top to bottom, then left to right: the order tiles are read, stacked on a phone, and saved. */
export function readingOrder(tiles) {
  return tiles
    .map((t, i) => [t, i])
    .sort(([a, i], [b, j]) => a.y - b.y || a.x - b.x || i - j)
    .map(([t]) => t);
}

/** Put one tile into place among tiles already placed: down past anything it hits, else up as far as it can go. */
function settle(tile, placed) {
  const t = { ...tile, y: Math.max(0, tile.y) };
  let hit = placed.filter((p) => overlaps(t, p));
  if (hit.length) {
    while (hit.length) {
      t.y = Math.max(...hit.map((p) => p.y + p.h));
      hit = placed.filter((p) => overlaps(t, p));
    }
    return t;
  }
  // Nothing in the way: rise to the lowest bottom edge above it in its columns.
  t.y = placed
    .filter((p) => sideBySide(t, p) && p.y + p.h <= t.y)
    .reduce((m, p) => Math.max(m, p.y + p.h), 0);
  return t;
}

/** Gravity over the whole layout, placing tiles in `order`; returns them in their original array order. */
export function compact(tiles, order) {
  const sorted = order ? [...tiles].sort(order) : readingOrder(tiles);
  const placed = [];
  for (const t of sorted) placed.push(settle(t, placed));
  const byKey = new Map(placed.map((t) => [t.key, t]));
  const out = tiles.map((t) => byKey.get(t.key));
  // Keep the same array (and object) identity when nothing moved, so callers can compare cheaply.
  return out.every((t, i) => t.x === tiles[i].x && t.y === tiles[i].y) ? tiles : out;
}

const fits = (tiles) => bottom(tiles) <= MAX_ROWS;

/** True when both lists hold the same tiles, in the same order, in the same places. */
export function sameLayout(a, b) {
  if (a === b) return true;
  if (!a || !b || a.length !== b.length) return false;
  return a.every((t, i) => {
    const u = b[i];
    return t.key === u.key && t.x === u.x && t.y === u.y && t.w === u.w && t.h === u.h;
  });
}

/**
 * Move a tile so its top-left corner is at (x, y), then apply gravity. A tile
 * moved down lets the tiles it meets at the same row go first, so it lands
 * under them; moved up or sideways, it goes first and pushes them down.
 * Returns the same array when nothing changed or the result would not fit.
 */
export function moveTile(tiles, key, x, y) {
  const i = tiles.findIndex((t) => t.key === key);
  if (i < 0) return tiles;
  const tile = tiles[i];
  const nx = clamp(Math.round(x), 0, COLUMNS - tile.w);
  const ny = clamp(Math.round(y), 0, MAX_ROWS - tile.h);
  if (nx === tile.x && ny === tile.y) return tiles;
  const next = tiles.map((t, j) => (j === i ? { ...t, x: nx, y: ny } : t));
  const down = ny > tile.y;
  const order = (a, b) => {
    if (a.y !== b.y) return a.y - b.y;
    if (a.key === key) return down ? 1 : -1;
    if (b.key === key) return down ? -1 : 1;
    return a.x - b.x;
  };
  const out = compact(next, order);
  return fits(out) && !sameLayout(out, tiles) ? out : tiles;
}

/**
 * Resize a tile to w x h (its top-left corner stays unless the width would
 * cross the right edge), clamped to its visual's minimum and the grid. The
 * resized tile keeps its place and pushes whatever it now covers down; then
 * gravity runs over everything.
 */
export function resizeTile(tiles, key, w, h) {
  const i = tiles.findIndex((t) => t.key === key);
  if (i < 0) return tiles;
  const tile = tiles[i];
  const min = minSize(tile);
  const nw = clamp(Math.round(w), min.w, COLUMNS);
  const nh = clamp(Math.round(h), min.h, MAX_TILE_HEIGHT);
  const nx = Math.min(tile.x, COLUMNS - nw);
  if (nw === tile.w && nh === tile.h && nx === tile.x) return tiles;
  const resized = { ...tile, x: nx, w: nw, h: nh };
  const placed = [resized];
  for (const t of readingOrder(tiles.filter((t) => t.key !== key))) placed.push(settle(t, placed));
  const byKey = new Map(placed.map((t) => [t.key, t]));
  const out = compact(tiles.map((t) => byKey.get(t.key)));
  return fits(out) ? out : tiles;
}

/** Where a w x h tile goes when added: the first free spot from the top of the last row of tiles, left to right. */
export function findSpot(tiles, w, h) {
  const start = tiles.length ? Math.max(...tiles.map((t) => t.y)) : 0;
  for (let y = start; y + h <= MAX_ROWS; y += 1) {
    for (let x = 0; x + w <= COLUMNS; x += 1) {
      const c = { x, y, w, h };
      if (!tiles.some((t) => overlaps(c, t))) return { x, y };
    }
  }
  return null;
}

export const canAddTile = (tiles) => tiles.length < MAX_TILES;

/**
 * Add a tile at its visual's default size (or the w/h it carries) in the next
 * free spot. Returns the new array, or null when the dashboard is full.
 */
export function addTile(tiles, tile) {
  if (!canAddTile(tiles)) return null;
  const size = tile.w && tile.h ? { w: tile.w, h: tile.h } : defaultSize(visualOf(tile));
  const spot = findSpot(tiles, size.w, size.h);
  if (!spot) return null;
  const out = compact([...tiles, { ...tile, ...size, ...spot }]);
  return fits(out) ? out : null;
}

export function removeTile(tiles, key) {
  const rest = tiles.filter((t) => t.key !== key);
  return rest.length === tiles.length ? tiles : compact(rest);
}

/**
 * Keyboard moves. Left and right move one column. Up and down swap the tile
 * with the nearest tile above or below it in its columns: with gravity a
 * one-row step would fall straight back, so a step is one tile.
 * `resize` grows or shrinks by one unit instead.
 */
export function nudge(tiles, key, direction, { resize = false } = {}) {
  const tile = tiles.find((t) => t.key === key);
  if (!tile) return tiles;
  if (resize) {
    const dw = direction === 'right' ? 1 : direction === 'left' ? -1 : 0;
    const dh = direction === 'down' ? 1 : direction === 'up' ? -1 : 0;
    return resizeTile(tiles, key, tile.w + dw, tile.h + dh);
  }
  if (direction === 'left') return moveTile(tiles, key, tile.x - 1, tile.y);
  if (direction === 'right') return moveTile(tiles, key, tile.x + 1, tile.y);
  const others = tiles.filter((t) => t.key !== key && sideBySide(t, tile));
  if (direction === 'up') {
    const above = others.filter((t) => t.y < tile.y);
    if (!above.length) return tiles;
    return moveTile(tiles, key, tile.x, Math.max(...above.map((t) => t.y)));
  }
  if (direction === 'down') {
    const below = others.filter((t) => t.y > tile.y);
    if (!below.length) return tiles;
    return moveTile(tiles, key, tile.x, Math.min(...below.map((t) => t.y)));
  }
  return tiles;
}

/* ---- pixels ---- */

export function columnWidth(width) {
  return Math.max(0, (width - GAP * (COLUMNS - 1)) / COLUMNS);
}

export const spanPx = (units, unit) => (units > 0 ? units * unit + (units - 1) * GAP : 0);

export function tileRect(tile, colWidth) {
  return {
    left: tile.x * (colWidth + GAP),
    top: tile.y * (ROW_HEIGHT + GAP),
    width: spanPx(tile.w, colWidth),
    height: spanPx(tile.h, ROW_HEIGHT),
  };
}

export const gridHeight = (tiles) => spanPx(bottom(tiles), ROW_HEIGHT);

/** A tile's height when tiles are stacked at full width on a narrow screen. */
export const stackedHeight = (tile) => spanPx(tile.h, ROW_HEIGHT);

/** For screen readers: "column 1, row 3, 6 columns wide, 5 rows tall". */
export function describePlace(tile) {
  const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;
  return `column ${tile.x + 1}, row ${tile.y + 1}, ${plural(tile.w, 'column')} wide, ${plural(tile.h, 'row')} tall`;
}
